import { useEffect, useState } from 'react';
import { Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import { Modal } from '../DashboardPrimitives';
import { customDashboardsAPI } from '../../../services/api';
import type { CustomDashboard } from './types';

interface ClarifyingQuestion {
  id: string;
  question: string;
  options?: string[];
  why?: string;
}

/**
 * The composer: one prompt → a whole dashboard.
 *
 * Generation fans out into a plan call plus one generate-validate-run cycle per
 * block, so it is genuinely slow (tens of seconds). The copy says so rather than
 * showing a bare spinner that looks hung.
 */

interface Suggestion {
  title: string;
  prompt: string;
}

/**
 * Shown only until the project's own suggestions load, and if that request fails.
 * These are generic by necessity — the real ones name this project's actual
 * attributes, metrics and tasks, so a user cannot click a suggestion the generator
 * will then refuse.
 */
const FALLBACK_SUGGESTIONS: Suggestion[] = [
  {
    title: 'Performance overview',
    prompt:
      'Highlight cards with total employees and average score this month, then a table of the 10 lowest scorers with their store and city, then a bar chart of red flags by type.',
  },
  {
    title: 'Attendance overview',
    prompt:
      'Cards for attendance % this month, days present and days absent. Then a bar chart of attendance % by city, and a table of employees with the most absent days.',
  },
];

const errorMessage = (e: unknown): string => {
  const status = (e as any)?.response?.status;
  const fromServer = (e as any)?.response?.data?.message;
  if (fromServer) return fromServer;
  // A bare 502/503/504 is the GATEWAY giving up, not our API answering. Generation
  // makes sequential model calls, so a long one can outlive the ingress timeout and
  // arrive as "Request failed with status code 503" — which tells the user nothing
  // and looks like a bug in their prompt.
  if (status === 502 || status === 503 || status === 504) {
    return 'The server took too long to answer and the request was cut off before it finished. This usually means the change was too large to work out in one go — try a smaller, more specific instruction.';
  }
  return (e as any)?.message || 'Something went wrong.';
};

const errorAlternatives = (e: unknown): string[] => {
  const raw = (e as any)?.response?.data?.alternatives;
  return Array.isArray(raw) ? raw.filter((a: unknown): a is string => typeof a === 'string') : [];
};

export default function CustomDashboardComposer({
  projectId,
  onClose,
  onCreated,
}: {
  projectId: number;
  onClose: () => void;
  onCreated: (dashboard: CustomDashboard, failures: Array<{ title: string; reason: string }>) => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [alternatives, setAlternatives] = useState<string[]>([]);

  // When the planner cannot decide something that changes the numbers, it asks
  // instead of guessing. The answers are sent back with the SAME prompt, so the user
  // never has to restate their request.
  const [questions, setQuestions] = useState<ClarifyingQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});

  const [suggestions, setSuggestions] = useState<Suggestion[]>(FALLBACK_SUGGESTIONS);
  useEffect(() => {
    let alive = true;
    customDashboardsAPI
      .suggestions(projectId)
      .then((res) => {
        const list = (res.data?.suggestions ?? []) as Suggestion[];
        if (alive && list.length > 0) setSuggestions(list);
      })
      .catch(() => { /* keep the fallback */ });
    return () => { alive = false; };
  }, [projectId]);

  const submit = async (text: string, clarifications: Array<{ question: string; answer: string }> = []) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    setError(null);
    setAlternatives([]);
    try {
      const res = await customDashboardsAPI.generate(trimmed, projectId, clarifications);
      if (res.data?.status === 'needs_clarification') {
        // Not an error — the planner is asking. Keep the prompt, show the questions.
        setQuestions(res.data.questions ?? []);
        setAnswers({});
        setBusy(false);
        return;
      }
      onCreated(res.data.dashboard as CustomDashboard, res.data.failures ?? []);
    } catch (e) {
      setError(errorMessage(e));
      setAlternatives(errorAlternatives(e));
      setBusy(false);
    }
  };

  const answered = questions.map((q) => ({
    question: q.question,
    answer: (answers[q.id] ?? '').trim(),
  }));
  // Every question must be answered — a half-answered set would just be re-asked.
  const allAnswered = questions.length > 0 && answered.every((a) => a.answer.length > 0);

  return (
    <Modal title="New dashboard" onClose={busy ? () => {} : onClose} maxWidth="max-w-2xl">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Describe the dashboard you want. Say what should sit at the top, what tables to
          show, and any charts — for example “highlight cards, then a table, then a bar graph”.
        </p>

        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={5}
          disabled={busy}
          placeholder="e.g. Cards with total employees and average score, then a table of the 10 lowest scorers, then a bar chart of red flags by type."
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none disabled:bg-gray-50"
        />

        {questions.length > 0 && !busy && (
          <div className="space-y-3 rounded-lg border border-primary-100 bg-primary-50/40 px-3 py-3">
            <p className="text-xs font-medium text-primary-900">
              A couple of things first — these change the numbers, so I don’t want to guess.
            </p>
            {questions.map((q) => (
              <div key={q.id}>
                <p className="text-xs font-medium text-gray-800">{q.question}</p>
                {q.why && <p className="mt-0.5 text-[11px] text-gray-500">{q.why}</p>}
                {q.options && q.options.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {q.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setAnswers((a) => ({ ...a, [q.id]: o }))}
                        className={`rounded-md border px-2 py-1 text-[11px] transition ${
                          answers[q.id] === o
                            ? 'border-primary-300 bg-primary-100 text-primary-800'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                )}
                {/* Free text is always available — the offered options are suggestions,
                    not an exhaustive list, and the user knows their project better. */}
                <input
                  value={answers[q.id] ?? ''}
                  onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                  placeholder={q.options?.length ? 'Or type your own answer…' : 'Your answer…'}
                  className="mt-1.5 w-full rounded-md border border-gray-200 px-2.5 py-1.5 text-xs focus:border-primary-500 focus:outline-none"
                />
              </div>
            ))}
            <div className="flex items-center justify-between gap-2 border-t border-primary-100 pt-2">
              <button
                type="button"
                onClick={() => setQuestions([])}
                className="text-[11px] font-medium text-gray-500 transition hover:text-gray-700"
              >
                Edit my request instead
              </button>
              <button
                type="button"
                onClick={() => void submit(prompt, answered)}
                disabled={!allAnswered}
                className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {!busy && questions.length === 0 && (
          <div>
            <p className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Or start from an example
            </p>
            <div className="space-y-1.5">
              {suggestions.map((s) => (
                <button
                  key={s.title}
                  type="button"
                  onClick={() => setPrompt(s.prompt)}
                  className="block w-full rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 text-left transition hover:border-primary-200 hover:bg-primary-50/40"
                >
                  <span className="block text-xs font-medium text-gray-800">{s.title}</span>
                  <span className="mt-0.5 block text-[11px] text-gray-500">{s.prompt}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-rose-100 bg-rose-50/70 px-3 py-2.5">
            <div className="flex items-start gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <p className="text-xs text-rose-800">{error}</p>
            </div>
            {alternatives.length > 0 && (
              <div className="mt-2 space-y-1.5 pl-6">
                <p className="text-[11px] font-medium text-rose-700">Try one of these instead:</p>
                {alternatives.map((a) => (
                  <button
                    key={a}
                    type="button"
                    onClick={() => setPrompt(a)}
                    className="block w-full rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-left text-xs text-rose-800 transition hover:bg-rose-50"
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <p className="text-[11px] text-gray-400">
            {busy
              ? 'Planning the layout, then writing and testing each block — this can take up to a minute.'
              : 'Created as a draft. Only you can see it until you publish.'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="rounded-md border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void submit(prompt)}
              disabled={busy || !prompt.trim() || questions.length > 0}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-primary-700 disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              {busy ? 'Building…' : 'Build dashboard'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
